import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getGantPredecessorIssues } from '../../../../../packages/common/api';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import { relationsSelectValues } from '../../../issuePage/components/IssueRelated/relationsSelectValues';

// Original class: relatedRow
const StyledRelatedRow = styled(Grid)(theme => ({
  '& .MuiGrid-item': {
    display: 'flex',
    alignItems: 'center',
    '& h5': {
      flexShrink: 0,
      display: 'inline-flex',
    },
    '& .MuiFormControl-root.MuiTextField-root.formTextField': {
      width: '156px',
      '& .MuiInputBase-root.MuiOutlinedInput-root .MuiInputBase-input.MuiOutlinedInput-input':
        {
          padding: '11px 16px',
        },
    },
  },
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        color: '#E4E4EF',
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-loading': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: customText
const StyledCustomText = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      lineHeight: '20px',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiInputBase-input.MuiOutlinedInput-input': {
        padding: '8px 16px!important',
        fontSize: '12px!important',
        color: '#E4E4EF!important',
        '-webkit-text-fill-color': '#E4E4EF!important',
        '&::placeholder': {
          color: '#E4E4EF!important',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF!important',
      },
    },
  },
}));
// Original class: hoveredIcon
const StyledHoveredIcon = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7174AC',
      },
    },
  },
}));
const GanttIssuePredecessor = ({
  issueId,
  projectId,
  socket,
  token,
  defaultPredecessors,
}) => {
  const getLocalString = useTranslation();
  const params = useParams();
  const [predecessors, setPredecessors] = useState([]);
  const [predecessorFieldOptions, setPredecessorFieldOptions] = useState([]);
  const [predecessorLoading, setPredecessorLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (defaultPredecessors.length > 0) {
      setPredecessorLoading(false);
      if (predecessors.length > 0) {
        setPredecessors(
          predecessors
            .filter(item => !item.deleted)
            .map(item => {
              const saved = defaultPredecessors.find(
                el => item.predecessorId === el.id,
              );
              if (saved !== undefined) {
                return {
                  predecessorId: saved.id,
                  predecessorName: saved.name,
                  delay: saved.delay,
                };
              } else {
                return item;
              }
            }),
        );
      } else {
        setPredecessors(
          defaultPredecessors.map(item => {
            return {
              predecessorId: item.id,
              predecessorName: item.name,
              delay: item.delay,
            };
          }),
        );
      }
    } else {
      setPredecessorLoading(false);
      setPredecessors(predecessors.filter(item => !item.deleted));
    }
  }, [defaultPredecessors]);

  const handlePredecessorFieldChange = useCallback(
    async value => {
      let params = {
        search: value,
      };
      issueId && (params.row = issueId);
      projectId && (params.project = projectId);
      try {
        const response = await getGantPredecessorIssues(params);
        if (response) {
          setPredecessorFieldOptions(response.data);
        }
      } catch (error) {
        console.error('ERROR WITH EDIT', error);
      }
    },
    [issueId, projectId],
  );

  const handlePredecessorSelect = useCallback(
    (value, index) => {
      const updatedObject = {
        ...predecessors[index],
        predecessorId: Number(value.valueId),
        predecessorName: value.valueName,
      };
      setPredecessors([
        ...predecessors.slice(0, index),
        updatedObject,
        ...predecessors.slice(index + 1),
      ]);
    },
    [predecessors],
  );
  const handleTextField = useCallback(
    (event, type, index) => {
      const updatedObject = {
        ...predecessors[index],
        [type]: event.target.value,
      };
      setPredecessors([
        ...predecessors.slice(0, index),
        updatedObject,
        ...predecessors.slice(index + 1),
      ]);
    },
    [predecessors],
  );
  const handleNumberField = useCallback(
    (event, type, field) => {
      const reg = new RegExp(/^[0-9-]*$/);
      if (reg.test(event.target.value)) {
        handleTextField(event, type, field);
      }
    },
    [handleTextField],
  );

  const handleSavePredecessor = useCallback(
    async item => {
      if (socket) {
        setPredecessorLoading(true);
        if (!item.predecessorId) {
          return;
        }
        let data = {
          predecessorId: item.predecessorId,
        };
        item.delay && (data.delay = Number(item.delay));
        if (item.new) {
          socket.emit('addGanttModalPredecessor', {
            token: token,
            groupId: projectId,
            rowId: issueId,
            projectId: Number(projectId),
            updatedValue: data,
          });
        } else {
          socket.emit('updateModalPredecessorDelay', {
            token: token,
            groupId: projectId,
            rowId: issueId,
            projectId: Number(projectId),
            updatedValue: data,
          });
        }
      }
    },
    [issueId, projectId, socket, token],
  );

  const handleEditPredecessor = useCallback(
    index => {
      const updatedObject = { ...predecessors[index], edit: true };
      setPredecessors([
        ...predecessors.slice(0, index),
        updatedObject,
        ...predecessors.slice(index + 1),
      ]);
    },
    [predecessors],
  );

  const handleDeletePredecessor = useCallback(
    async index => {
      if (predecessors[index].new) {
        const newArray = predecessors.filter((value, i) => i !== index);
        setPredecessors(newArray);
        return;
      }
      const predecessor = { ...predecessors[index], deleted: true };
      setPredecessors([
        ...predecessors.slice(0, index),
        predecessor,
        ...predecessors.slice(index + 1),
      ]);
      if (socket) {
        setPredecessorLoading(true);
        socket.emit('deleteGanttModalPredecessor', {
          token: token,
          groupId: projectId,
          rowId: issueId,
          projectId: Number(projectId),
          updatedValue: {
            predecessorId: predecessors[index].predecessorId,
          },
        });
      }
    },
    [issueId, predecessors, projectId, socket, token],
  );
  const handleAddPredecessor = useCallback(() => {
    setPredecessors([
      { new: true, predecessorId: '', predecessorName: '', delay: '' },
      ...predecessors,
    ]);
  }, [predecessors]);

  return (
    <>
      <Grid
        container
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid>
          <Typography
            variant='h3'
            sx={{
              color: '#082253',
              fontWeight: 600,
            }}
          >
            {getLocalString('GanttContainer', 'labels', 'predecessor')}
          </Typography>
        </Grid>
        <Grid>
          <IconButton
            onClick={handleAddPredecessor}
            sx={{ padding: '6px 4px' }}
          >
            <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container direction={'column'} sx={{ position: 'relative' }}>
        {predecessorLoading && (
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              zIndex: 99,
              position: 'absolute',
            }}
          >
            <CircularProgress color='secondary' />
          </Grid>
        )}
        {error && (
          <Box sx={{ position: 'absolute', top: '-10px', left: 0 }}>
            <Typography
              variant='h3'
              sx={{
                color: '#E03737',
              }}
            >
              {getLocalString('errors', 'relatedError')}
            </Typography>
          </Box>
        )}

        {predecessors.length > 0 &&
          predecessors.map((item, index) => {
            const disabledTextField = item.new
              ? false
              : item.edit
                ? false
                : true;
            return (
              <StyledRelatedRow
                key={index}
                container
                columnSpacing={4}
                sx={{
                  marginTop: '10px',
                }}
              >
                <Grid
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      color: '#41424E',
                      marginRight: '5px',
                    }}
                  >
                    {`${getLocalString('labels', 'issue')} #`}
                  </Typography>
                  <StyledFilterSelect
                    noOptionsText={getLocalString('labels', 'notFound')}
                    loadingText={getLocalString('labels', 'loading')}
                    filterOptions={x => x}
                    onChange={(event, newValue) =>
                      handlePredecessorSelect(newValue, index)
                    }
                    disablePortal
                    options={predecessorFieldOptions}
                    disableClearable
                    disabled={!item.hasOwnProperty('new')}
                    sx={{ width: '156px' }}
                    onBlur={() => setPredecessorFieldOptions([])}
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          {...props}
                          key={option.valueId + option.valueName}
                          value={option.valueId}
                          selected={option.valueId === item.valueId}
                        >
                          {option.valueName}
                        </MenuItem>
                      );
                    }}
                    getOptionLabel={option => option.valueName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        onChange={ev =>
                          handlePredecessorFieldChange(ev.target.value)
                        }
                        placeholder={getLocalString('labels', 'select')}
                      />
                    )}
                    popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                    value={
                      item.predecessorId
                        ? {
                            valueId: item.predecessorId,
                            valueName: item.predecessorName,
                          }
                        : ''
                    }
                    slots={{
                      popper: props => (
                        <Popper {...props} placement='bottom-start'></Popper>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      color: '#41424E',
                      marginRight: '5px',
                    }}
                  >
                    {`${getLocalString('labels', 'delay')}:`}
                  </Typography>
                  <StyledCustomText
                    disabled={disabledTextField}
                    sx={{ width: '80px!important' }}
                    onChange={event => handleNumberField(event, 'delay', index)}
                    placeholder={getLocalString('labels', 'input')}
                    fullWidth
                    variant='outlined'
                    required
                    value={item.delay ? item.delay : ''}
                    className={'formTextField labelLeft'}
                    slotProps={{
                      inputLabel: { shrink: false },
                    }}
                  />
                  <Typography
                    variant='h5'
                    sx={{
                      color: '#41424E',
                      marginLeft: '5px',
                    }}
                  >
                    {getLocalString('labels', 'days')}
                  </Typography>
                </Grid>
                <Grid>
                  <Grid container>
                    {item.edit || item.new ? (
                      <Grid>
                        <IconButton
                          disabled={!item.predecessorId}
                          onClick={() => handleSavePredecessor(item)}
                          sx={{ padding: '8px', height: '100%' }}
                        >
                          <SubmitIcon />
                        </IconButton>
                      </Grid>
                    ) : (
                      <Grid>
                        <IconButton
                          onClick={() => handleEditPredecessor(index)}
                          sx={{ padding: '8px', height: '100%' }}
                        >
                          <UserNewEditIcon
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                          />
                        </IconButton>
                      </Grid>
                    )}
                    <Grid>
                      <StyledHoveredIcon
                        onClick={() => handleDeletePredecessor(index)}
                        sx={{ padding: '8px', height: '100%' }}
                      >
                        <DeleteIconBig />
                      </StyledHoveredIcon>
                    </Grid>
                  </Grid>
                </Grid>
              </StyledRelatedRow>
            );
          })}
      </Grid>
    </>
  );
};

export default GanttIssuePredecessor;
